import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw, X } from "lucide-react";
import { inquiriesApi } from "./api";
import type { InquiryDetail, InquiryListRow, InquiryReply } from "./types";
import { StatusBadge } from "../applications/shared/StatusBadge";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field } from "../applications/shared/InfoBlock";
import {
  formatApplicantName,
  formatDate,
  formatDateTime,
} from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useDebounce } from "@/lib/utils/useDebounce";

const STATUS_OPTIONS = [
  { value: "new", label: "Nuevo" },
  { value: "read", label: "Leído" },
  { value: "replied", label: "Respondido" },
  { value: "archived", label: "Archivado" },
];

const SUBJECT_LABELS: Record<string, string> = {
  event_inquiry: "Consulta sobre evento",
  refund_request: "Pedido de reembolso",
  event_problem: "Problema en evento",
  report_organizer: "Reporte sobre organizador",
  other: "Otro",
};

const PAGE_SIZE = 25;

export function MessagesScreen() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => setPage(1), [debouncedSearch, status]);

  const filters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      status: status || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [debouncedSearch, status, page],
  );

  const listQuery = useQuery({
    queryKey: ["communications", "inquiries", filters],
    queryFn: () => inquiriesApi.list(filters),
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ["communications", "inquiries", "detail", selectedId],
    queryFn: () => inquiriesApi.detail(selectedId!),
    enabled: selectedId !== null,
  });

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Comunicaciones · Mensajes</p>
          <h1 className="nx-h1">Mensajes a productores</h1>
          <p className="text-sm text-fg-muted mt-1">
            Auditoría de la mensajería pública. Mixcore es intermediario: los
            visitantes escriben acá, los productores responden desde el Hub.
          </p>
        </div>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por remitente, productor o contenido…"
        status={status}
        onStatusChange={setStatus}
        statusOptions={STATUS_OPTIONS}
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando mensajes…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<InquiryListRow>
          rows={listQuery.data?.inquiries ?? []}
          columns={columns}
          page={listQuery.data?.page ?? 1}
          pageSize={listQuery.data?.pageSize ?? PAGE_SIZE}
          total={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
          emptyLabel="No hay mensajes que coincidan con los filtros."
        />
      )}

      {selectedId !== null && (
        <InquiryDrawer
          loading={detailQuery.isLoading}
          inquiry={detailQuery.data?.inquiry ?? null}
          replies={detailQuery.data?.replies ?? []}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

const columns: Column<InquiryListRow>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "subject",
    header: "Asunto",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{SUBJECT_LABELS[r.subject] ?? r.subject}</div>
        <div className="text-2xs text-fg-subtle line-clamp-1 max-w-md">{r.messagePreview}</div>
      </div>
    ),
  },
  {
    key: "from",
    header: "De",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.senderName}</div>
        <div className="text-2xs text-fg-subtle break-all">{r.senderEmail}</div>
      </div>
    ),
  },
  {
    key: "to",
    header: "Para (productor)",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.producerName || "—"}</div>
        <div className="text-2xs text-fg-subtle break-all">{r.producerEmail ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => <StatusBadge status={r.status} />,
    width: "min",
  },
  {
    key: "updatedAt",
    header: "Actualizado",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.updatedAt)}</span>,
    width: "min",
    align: "right",
  },
];

function InquiryDrawer({
  loading,
  inquiry,
  replies,
  onClose,
}: {
  loading: boolean;
  inquiry: InquiryDetail | null;
  replies: InquiryReply[];
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[560px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">
              {inquiry ? (SUBJECT_LABELS[inquiry.subject] ?? inquiry.subject) : "Mensaje"}
            </h2>
            {inquiry && (
              <p className="text-2xs text-fg-subtle mt-0.5">
                #{inquiry.id} · {formatDateTime(inquiry.createdAt)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading || !inquiry ? (
            <div className="flex items-center justify-center py-12 text-fg-subtle">
              <Loader2 size={16} className="animate-spin mr-2" />
              Cargando…
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2">
                <StatusBadge status={inquiry.status} />
              </div>

              <Section title="Conversación">
                <Field label="De" value={`${inquiry.senderName} <${inquiry.senderEmail}>`} wide />
                <Field
                  label="Para (productor)"
                  value={
                    inquiry.producer
                      ? `${formatApplicantName(inquiry.producer)} <${inquiry.producer.email}>`
                      : "—"
                  }
                  wide
                />
              </Section>

              <ThreadView inquiry={inquiry} replies={replies} />

              <Section title="Tracking">
                <Field label="Leído" value={inquiry.readAt ? formatDateTime(inquiry.readAt) : "—"} />
                <Field label="Última actualización" value={formatDateTime(inquiry.updatedAt)} />
                <Field label="IP" value={inquiry.ipAddress ?? "—"} />
                <Field label="User agent" value={<span className="text-2xs break-all">{inquiry.userAgent ?? "—"}</span>} wide />
              </Section>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function ThreadView({
  inquiry,
  replies,
}: {
  inquiry: InquiryDetail;
  replies: InquiryReply[];
}) {
  return (
    <div className="flex flex-col gap-3">
      <Bubble
        author={inquiry.senderName}
        role="visitor"
        body={inquiry.message}
        at={inquiry.createdAt}
      />
      {replies.map((r) => (
        <Bubble
          key={r.id}
          author={r.authorNameSnapshot || roleLabel(r.authorRole)}
          role={r.authorRole}
          body={r.body}
          at={r.createdAt}
        />
      ))}
    </div>
  );
}

function roleLabel(role: string): string {
  return role === "producer" ? "Productor" : role === "visitor" ? "Visitante" : "Mixcore staff";
}

function Bubble({
  author,
  role,
  body,
  at,
}: {
  author: string;
  role: string;
  body: string;
  at: string;
}) {
  const isProducer = role === "producer" || role === "staff";
  return (
    <div className={`flex flex-col ${isProducer ? "items-end" : "items-start"}`}>
      <div className="text-2xs text-fg-subtle mb-1 px-1">
        {author} <span className="text-fg-muted">· {roleLabel(role)} · {formatDateTime(at)}</span>
      </div>
      <div
        className={`max-w-[90%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
          isProducer
            ? "bg-accent-soft text-fg border border-accent/30"
            : "bg-bg-subtle text-fg border border-border-subtle"
        }`}
      >
        {body}
      </div>
    </div>
  );
}
